import { useCallback, useEffect, useRef, useState } from "react";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { backspaceInField, insertIntoField } from "./tvKeyboardText.js";
import {
  hideDeviceKeyboard,
  installDeclinedOkWatch,
  platformKeyboardFailed,
} from "./tvKeyboardWatch.js";
import { useTvKeyboard } from "./tvKeyboardStore.js";

/**
 * A keyboard the D-pad can actually use, for devices whose own keyboard cannot
 * be typed on with a remote (see tvKeyboard.ts for the measurement).
 *
 * A TV keyboard, not a phone one: every key is a button, so the arrows walk them
 * and OK presses the focused one, and the focus ring never leaves the panel. The
 * letters and the number/symbol layer are two rows apart so a URL or a password
 * is reachable in a handful of presses.
 *
 * WHAT IT IS NOT, deliberately, because almost nobody needs it. It never
 * replaces the platform keyboard where that one works: it is raised only on the
 * measured failure (the platform keyboard refusing OK), only inside the app,
 * only once per field opening, and never on a screen a finger can touch
 * (`maxTouchPoints`) or that has produced a pointer press — so a phone, a
 * tablet, a desktop and any TV whose keyboard takes the D-pad are untouched and
 * unchanged. Nothing but the preference is
 * stored, and the panel is not in the DOM at all until something is wrong.
 *
 * The website gets it only if a viewer asks: `Settings → Text entry` sets it to
 * When needed (default), Always or Never. A browser with a keyboard can produce
 * the same key pattern with no fault at all, which is why the automatic path is
 * app-only (see tvKeyboardWatch.ts).
 *
 * Focus and Back come from the app's own dialog conventions (`useDialogFocus`),
 * so Android's Back closes this the way it closes the guide's detail card, and
 * focus returns to the field it was typing into. Two things about the ring are
 * deliberate, both measured on a TV with a real remote (2026-09-22): the rows
 * share one column grid (so Down from `r` reaches `f`, and not the nearer-looking
 * `d` that centring the rows produced), and OK on the layer key puts the ring on
 * the first key of the layer it switched to instead of leaving it on the layer
 * key with the new symbols drawn but out of reach.
 */

const LETTER_ROWS: readonly (readonly string[])[] = [
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
  ["z", "x", "c", "v", "b", "n", "m"],
];

/**
 * Digits and the FULL ASCII punctuation set — all 32 of them — because the two
 * things viewers type here are a server URL and a password, and both are full
 * of them (owner, 2026-09-22: "are symbols there like :, which is in a lot of
 * server urls. !, -, _, $, @, %, ., /, \\. ... common characters that may be
 * used in passwords?"). The first spelling of this row was missing `%` and
 * `\` — percent-encoding and a Windows path separator, both plausible in a URL
 * — so the set is now enumerated rather than chosen by feel, and a test asserts
 * none of the 32 can go missing again.
 *
 * Ordered by what is reached first: digits, then the URL characters one press
 * down (`.` `/` `:` `-` `_` `@` `%` `&` `=` `+`), then the security-shaped ones
 * (`! ? # $ ( ) ' " ; ,`), and the long tail (12 keys) last.
 */
const SYMBOL_ROWS: readonly (readonly string[])[] = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  [".", "/", ":", "-", "_", "@", "%", "&", "=", "+"],
  ["!", "?", "#", "$", "(", ")", "'", '"', ";", ","],
  ["\\", "~", "^", "`", "|", "<", ">", "[", "]", "{", "}", "*"],
];

/**
 * Done on a search box searches (owner, 2026-10-13: "hitting done isn't searching"). The device
 * keyboard's Done submits the form around a search field; this keyboard only closed. Only a
 * search form is submitted: a setup form with three fields must not be sent half-filled by
 * pressing Done after the first, so there Done stays "put the keyboard away".
 */
export function submitSearch(field: HTMLInputElement | HTMLTextAreaElement | null): void {
  if (!field || !field.isConnected) return;
  const form = field.closest("form");
  if (!form || form.getAttribute("role") !== "search") return;
  if (field.value.trim() === "") return;
  if (typeof form.requestSubmit === "function") form.requestSubmit();
  else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
}

export function TvKeyboard() {
  const open = useTvKeyboard((s) => s.open);
  const target = useTvKeyboard((s) => s.target);
  const close = useTvKeyboard((s) => s.close);
  const rootRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [symbols, setSymbols] = useState(false);
  const [shift, setShift] = useState(false);
  /** Set only by the layer key, so the ring moves for a layer press and not for
   * the reset that happens when a fresh opening starts on letters. */
  const layerPressed = useRef(false);

  useDialogFocus({ open, onClose: close, rootRef });

  // What is typed, shown ON the keyboard. The panel covers the bottom half of
  // a 480-px TV screen, so a field there sat under it and the viewer typed
  // blind (projector, 2026-09-27). Read from the field on every input, so
  // the device's own typing and a paste show here too.
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (!open || !target) return;
    const read = () => setTyped(target.value);
    read();
    target.addEventListener("input", read);
    return () => target.removeEventListener("input", read);
  }, [open, target]);
  const typedRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    const line = typedRef.current;
    if (line) line.scrollLeft = line.scrollWidth;
  }, [typed]);

  // OK on the layer key must PUT the ring in the layer it switched to. It used
  // to stay on that key (now labelled ABC), leaving the symbols on screen but
  // unreachable except by pressing Up — measured on the ADT-3 (2026-09-22): the
  // keys were drawn, the ring never entered them. The first key of the new layer
  // is the top-left of its grid, so Down then walks the rows the viewer sees.
  useEffect(() => {
    if (!layerPressed.current) return;
    layerPressed.current = false;
    gridRef.current?.querySelector<HTMLElement>(".tvkeyboard__key")?.focus();
  }, [symbols]);

  // The watch runs for the life of the app, not only while the keyboard is up:
  // it is what notices the platform keyboard failing in the first place.
  useEffect(
    () =>
      installDeclinedOkWatch({
        onDeclinedOk: (field) => useTvKeyboard.getState().openFor(field),
        onOpenedByKey: (field) => {
          const state = useTvKeyboard.getState();
          // `always` is the viewer asking for it. So is a device whose own
          // keyboard has already refused a press in this session: waiting to be
          // refused AGAIN would spend a press that does nothing on every field
          // the viewer opens, and on this device there is no other way to type.
          if (state.mode === "always" || platformKeyboardFailed()) state.openFor(field);
        },
      }),
    [],
  );

  // A fresh opening always starts on letters, unshifted, like a phone's, and
  // ONE keyboard is on screen: the device's own is up on the very devices this
  // exists for (it opens on the field and cannot be typed on), so it is asked to
  // go away as ours arrives.
  useEffect(() => {
    if (!open) return;
    setSymbols(false);
    setShift(false);
    hideDeviceKeyboard();
  }, [open]);

  const press = useCallback((text: string) => {
    const field = useTvKeyboard.getState().target;
    if (field?.isConnected) insertIntoField(field, text);
  }, []);

  const erase = useCallback(() => {
    const field = useTvKeyboard.getState().target;
    if (field?.isConnected) backspaceInField(field);
  }, []);

  if (!open || !target) return null;

  const rows = symbols ? SYMBOL_ROWS : LETTER_ROWS;
  const label = (character: string): string => (shift && !symbols ? character.toUpperCase() : character);

  return (
    <div className="tvkeyboard" role="dialog" aria-modal="true" aria-label="On-screen keyboard" data-tvkeyboard="" ref={rootRef}>
      {/* The words matter here (owner, 2026-09-22): a store tester scrolling past
          this must read it as the remote-optimised keyboard it is, not as an
          admission that the app is broken. It is also only ever on screen on a
          device whose own keyboard has just refused the remote, so "if yours
          doesn't work, try ours" is literally the situation the viewer is in. */}
      <div className="tvkeyboard__echo">
        <p className="tvkeyboard__typed" aria-live="polite" ref={typedRef}>
          {typed ? (
            target.type === "password" ? "•".repeat(typed.length) : typed
          ) : (
            <span className="tvkeyboard__placeholder">{target.placeholder || target.getAttribute("aria-label") || ""}</span>
          )}
          <span className="tvkeyboard__caret" aria-hidden />
        </p>
        <p className="tvkeyboard__hint">Remote-optimized keyboard. If your keyboard doesn't work, try ours.</p>
      </div>
      <div className="tvkeyboard__grid" ref={gridRef}>
        {rows.map((row, index) => (
          <div className="tvkeyboard__row" key={index}>
            {row.map((character) => {
              const shown = label(character);
              return (
                <button
                  key={character}
                  type="button"
                  className="tvkeyboard__key"
                  data-key={character}
                  aria-label={shown}
                  onClick={() => press(shown)}
                >
                  {shown}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="tvkeyboard__row tvkeyboard__row--actions">
        <button
          type="button"
          className="tvkeyboard__key tvkeyboard__key--action"
          data-key="layer"
          aria-label={symbols ? "Letters" : "Numbers and symbols"}
          onClick={() => {
            layerPressed.current = true;
            setSymbols((on) => !on);
          }}
        >
          {symbols ? "ABC" : "123"}
        </button>
        {!symbols && (
          <button
            type="button"
            className="tvkeyboard__key tvkeyboard__key--action"
            data-key="shift"
            aria-label="Capitals"
            aria-pressed={shift}
            onClick={() => setShift((on) => !on)}
          >
            ⇧
          </button>
        )}
        <button
          type="button"
          className="tvkeyboard__key tvkeyboard__key--wide"
          data-key=" "
          aria-label="Space"
          onClick={() => press(" ")}
        >
          Space
        </button>
        <button
          type="button"
          className="tvkeyboard__key tvkeyboard__key--action"
          data-key="backspace"
          aria-label="Backspace"
          onClick={erase}
        >
          ⌫
        </button>
        <button
          type="button"
          className="tvkeyboard__key tvkeyboard__key--done"
          data-key="done"
          aria-label="Close the keyboard"
          onClick={() => {
            const field = useTvKeyboard.getState().target;
            close();
            submitSearch(field);
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
